import { useState, useEffect, useCallback, useRef } from 'react';
import getConfig from 'next/config';
import logger from '../utils/logger';
const { publicRuntimeConfig } = getConfig() || {};

// Chargebee SDK types
declare global {
    interface Window {
        Chargebee?: {
            init: (config: {
                site: string;
                publishableKey: string;
            }) => ChargebeeInstance;
            getInstance: () => ChargebeeInstance | null;
        };
    }
}

interface ChargebeeInstance {
    load: (
        module: 'google-pay' | 'apple-pay' | 'components'
    ) => Promise<PaymentHandler>;
    createComponent: (type: string, options?: object) => CardComponent;
}

interface PaymentHandler {
    setPaymentIntent: (intent: object) => void;
    getPaymentIntent: () => object | null;
    canMakePayments: () => Promise<boolean>;
    mountPaymentButton: (
        selector: string,
        options: {
            buttonType?: string;
            buttonColor?: string;
            buttonLocale?: string;
            locale?: string;
            recurringPaymentRequest?: {
                paymentDescription: string;
                regularBilling: { label: string; amount: string };
                billingAgreement: string;
                managementURL?: string;
            };
        },
        paymentOptions?: {
            emailRequired?: boolean;
            billingAddressRequired?: boolean;
        }
    ) => Promise<void>;
    handlePayment: () => Promise<{
        token?: string;
        paymentToken?: string;
        paymentIntent?: {
            id: string;
            gateway_account_id: string;
            active_payment_attempt?: {
                id_at_gateway?: string;
            };
        };
    }>;
}

interface CardComponent {
    mount: (selector: string) => void;
    on: (event: string, callback: (data: unknown) => void) => void;
    tokenize: () => Promise<{ token: string }>;
}

interface UseChargebeeReturn {
    isReady: boolean;
    isLoading: boolean;
    error: string | null;
    chargebeeInstance: ChargebeeInstance | null;
    loadGooglePay: () => Promise<PaymentHandler | null>;
    loadApplePay: () => Promise<PaymentHandler | null>;
    site: string;
    publishableKey: string;
    isAppleDevice: boolean;
    isMobileApp: boolean;
}

/**
 * Detect if we're on an Apple device
 */
function detectAppleDevice(): boolean {
    if (typeof navigator === 'undefined') return false;

    const platform = navigator.platform?.toUpperCase() || '';
    const userAgent = navigator.userAgent || '';

    const isMacOS = platform.includes('MAC');
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);

    return isMacOS || isIOS;
}

/**
 * Detect if we're in a mobile app or mobile browser
 */
function detectMobileApp(): boolean {
    if (typeof navigator === 'undefined') return false;

    const userAgent = navigator.userAgent || '';

    // Check for our app's identifier
    const isNiioApp =
        userAgent.includes('NiioArt') || userAgent.includes('com.niioart.app');

    // Check for WebView indicators
    const isWebView =
        userAgent.includes('wv') ||
        (userAgent.includes('Android') && userAgent.includes('Mobile'));

    // Check for any mobile browser
    const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            userAgent
        );

    return isNiioApp || isWebView || isMobile;
}

/**
 * Hook to initialize and manage Chargebee SDK
 * Based on the web app's billing.form.client.controller.js logic
 */
export function useChargebee(): UseChargebeeReturn {
    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const instanceRef = useRef<ChargebeeInstance | null>(null);
    const initAttemptedRef = useRef(false);

    // Device detection (computed once)
    const isAppleDevice = detectAppleDevice();
    const isMobileApp = detectMobileApp();

    // Get Chargebee configuration
    const config = publicRuntimeConfig?.niioConfig || {};
    const site = config.chargebee_site || '';
    const publishableKey = config.chargebee_publishable_key || '';

    // Wait for Chargebee SDK to be available
    // Script is loaded in _document.tsx like the main app does
    const waitForChargebee = useCallback((): Promise<void> => {
        return new Promise((resolve, reject) => {
            // Check if already loaded
            if (window.Chargebee) {
                resolve();
                return;
            }

            // Wait for it to load (max 10 seconds)
            let attempts = 0;
            const maxAttempts = 100;
            const checkInterval = setInterval(() => {
                attempts++;
                if (window.Chargebee) {
                    clearInterval(checkInterval);
                    resolve();
                } else if (attempts >= maxAttempts) {
                    clearInterval(checkInterval);
                    reject(
                        new Error(
                            'Chargebee SDK failed to load - check _document.tsx'
                        )
                    );
                }
            }, 100);
        });
    }, []);

    // Initialize Chargebee
    // Following the web app pattern: use Chargebee.init() directly, not getInstance()
    const initChargebee = useCallback(async () => {
        if (initAttemptedRef.current) return;
        initAttemptedRef.current = true;

        if (!site || !publishableKey) {
            console.error('[CHARGEBEE] Missing configuration:', {
                site,
                publishableKey,
            });
            setError('Chargebee configuration missing');
            setIsLoading(false);
            return;
        }

        try {
            logger.debug('[CHARGEBEE] Initializing with site:', site);
            logger.debug(
                '[CHARGEBEE] publishableKey:',
                publishableKey
                    ? `${publishableKey.substring(0, 10)}...`
                    : 'MISSING'
            );
            await waitForChargebee();

            if (!window.Chargebee) {
                throw new Error('Chargebee SDK not loaded');
            }

            logger.debug(
                '[CHARGEBEE] SDK loaded, initializing directly with Chargebee.init()...'
            );

            // Use Chargebee.init() directly like the web app does
            // Don't use getInstance() - the web app never uses it
            const instance = window.Chargebee.init({
                site,
                publishableKey,
            });

            logger.debug(
                '[CHARGEBEE] init() returned:',
                instance ? 'Instance' : 'null/undefined'
            );
            if (instance) {
                logger.debug(
                    '[CHARGEBEE] Instance keys:',
                    Object.keys(instance)
                );
            }

            if (!instance) {
                throw new Error(
                    'Chargebee instance not created. Check: 1) site/key match, 2) CSP headers, 3) single script load'
                );
            }

            instanceRef.current = instance;
            setIsReady(true);
            logger.debug('[CHARGEBEE] Initialized successfully');
            logger.debug(
                '[CHARGEBEE] Instance methods:',
                Object.keys(instance)
            );
            logger.debug('[CHARGEBEE] Device detection:', {
                isAppleDevice,
                isMobileApp,
            });
        } catch (err) {
            console.error('[CHARGEBEE] Initialization error:', err);
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to initialize Chargebee'
            );
        } finally {
            setIsLoading(false);
        }
    }, [site, publishableKey, waitForChargebee, isAppleDevice, isMobileApp]);

    // Initialize on mount
    useEffect(() => {
        initChargebee();
    }, [initChargebee]);

    // Load Google Pay handler
    // Following web app logic: Google Pay is enabled unconditionally
    // Chargebee's SDK handles browser/device compatibility internally
    const loadGooglePay =
        useCallback(async (): Promise<PaymentHandler | null> => {
            if (!instanceRef.current) {
                console.error('[CHARGEBEE] Instance not ready for Google Pay');
                return null;
            }

            try {
                logger.debug('[CHARGEBEE] Loading Google Pay module');
                logger.debug(
                    '[CHARGEBEE] Google Pay enabled unconditionally - Chargebee will determine actual support'
                );

                const handler = await instanceRef.current.load('google-pay');
                logger.debug('[CHARGEBEE] Google Pay handler loaded:', handler);
                logger.debug('[CHARGEBEE] Handler type:', typeof handler);
                logger.debug(
                    '[CHARGEBEE] Handler methods available:',
                    Object.keys(handler || {})
                );
                logger.debug(
                    '[CHARGEBEE] Has setPaymentIntent:',
                    typeof (handler as any)?.setPaymentIntent
                );
                logger.debug(
                    '[CHARGEBEE] Has mountPaymentButton:',
                    typeof (handler as any)?.mountPaymentButton
                );

                // Check if handler has required methods
                if (typeof (handler as any)?.setPaymentIntent !== 'function') {
                    console.warn(
                        '[CHARGEBEE] Google Pay handler missing required methods. Handler only has:',
                        Object.keys(handler || {})
                    );
                    console.warn(
                        '[CHARGEBEE] This may indicate Chargebee SDK issue or Google Pay not configured in Chargebee dashboard'
                    );
                }

                return handler;
            } catch (err) {
                console.error('[CHARGEBEE] Failed to load Google Pay:', err);
                return null;
            }
        }, []);

    // Load Apple Pay handler
    // Following web app logic: Apple Pay is enabled on any Apple device
    const loadApplePay =
        useCallback(async (): Promise<PaymentHandler | null> => {
            if (!instanceRef.current) {
                console.error('[CHARGEBEE] Instance not ready for Apple Pay');
                return null;
            }

            // Check if we're on an Apple device (following web app logic)
            if (!isAppleDevice) {
                logger.debug(
                    '[CHARGEBEE] Not an Apple device, skipping Apple Pay'
                );
                return null;
            }

            try {
                logger.debug('[CHARGEBEE] Loading Apple Pay module');
                logger.debug(
                    '[CHARGEBEE] Apple Pay enabled on Apple device - Chargebee will determine actual support'
                );

                const handler = await instanceRef.current.load('apple-pay');
                logger.debug('[CHARGEBEE] Apple Pay handler loaded:', handler);
                logger.debug(
                    '[CHARGEBEE] Handler methods available:',
                    Object.keys(handler || {})
                );

                // Note: We don't check canMakePayments() here because the web app enables Apple Pay
                // on any Apple device and lets Chargebee handle the actual capability check

                return handler;
            } catch (err) {
                console.error('[CHARGEBEE] Failed to load Apple Pay:', err);
                return null;
            }
        }, [isAppleDevice]);

    return {
        isReady,
        isLoading,
        error,
        chargebeeInstance: instanceRef.current,
        loadGooglePay,
        loadApplePay,
        site,
        publishableKey,
        isAppleDevice,
        isMobileApp,
    };
}

export default useChargebee;
